/**
 * Controls for the native-scroll product sliders.
 *
 * Every product carousel on the site is a Slick slider with swiping, dragging
 * and arrows switched off; the CSS in app.css turns its `.slick-list` into a
 * plain horizontal scroll container with a hidden scrollbar. That left one way
 * to move the row: a horizontal wheel gesture, which only a trackpad or a Magic
 * Mouse produces. A regular mouse has no horizontal wheel, so on Windows (and
 * on a Mac with a mouse) the row simply did not move, while the cursor showed a
 * grab hand that promised a drag that never came.
 *
 * This file gives a mouse user three ways to move the row:
 *
 * 1. Drag the row itself. Only mouse and pen pointers drag here; touch keeps
 *    the browser's own scrolling, because on a phone that already works and a
 *    second handler would only fight it.
 * 2. A real scrollbar under the row: the thumb drags, a click on the rail jumps
 *    one page in that direction, and the arrow keys move it when it has focus.
 *    The rail is a thin bar with a tall hit area around it, so it is easy to
 *    grab without looking heavy.
 * 3. Two large arrow buttons on the row's edges, at mid-height, that appear
 *    while the mouse is over the row. That is the pattern a Dutch shopper knows
 *    from Bol and Coolblue and it is far easier to find than a small button
 *    next to the bar.
 *
 * `window.florafySliderControls.bind(slider)` is idempotent per `.slick-list`.
 * app.js re-initialises Slick on every width change, and Slick's `unslick`
 * replaces the list element, so a rebind after a resize must discard the old
 * controls and build new ones; otherwise the scrollbar and the buttons keep
 * listening to an element that is no longer in the document.
 *
 * Pure decisions (how far a click scrolls, when a pointer movement counts as a
 * drag, where the thumb sits, how a thumb drag maps to a scroll position) are
 * exposed on the same object so the unit test can exercise them without a DOM.
 */
(function () {
  'use strict';

  /** Pointer travel below this, in px, is still a click and not a drag. */
  var DRAG_THRESHOLD_PX = 6;

  /** The thumb never gets narrower than this share of the rail, so it stays easy to grab. */
  var MIN_THUMB_PERCENT = 20;

  /** Class names shared with app.css. */
  var HOST_CLASS = 'slider-controls-host';
  var CONTROLS_CLASS = 'slider-controls';
  var TRACK_CLASS = 'slider-progress-track';
  var RAIL_CLASS = 'slider-progress-rail';
  var THUMB_CLASS = 'slider-progress-thumb';
  var EDGE_BUTTON_CLASS = 'slider-edge-button';
  var DRAGGING_CLASS = 'is-dragging';
  var THUMB_DRAGGING_CLASS = 'is-thumb-dragging';

  var FALLBACK_LABELS = { previous: 'Vorige', next: 'Volgende', scrollbar: 'Door de rij scrollen' };

  /** Gives a list without an id one, so the scrollbar can name what it controls. */
  var listIdCounter = 0;

  /**
   * How far one button press or rail click scrolls: as many whole slides as
   * fit in the viewport, never less than one slide. Scrolling by the full
   * viewport width would cut a card in half on both edges; scrolling by whole
   * slides keeps the grid aligned.
   *
   * @param {number} clientWidth visible width of the list
   * @param {number} slideStep distance from one slide's left edge to the next
   * @returns {number} px
   */
  function scrollStep(clientWidth, slideStep) {
    if (!(slideStep > 0)) return clientWidth;
    var visible = Math.floor(clientWidth / slideStep);
    return Math.max(1, visible) * slideStep;
  }

  /**
   * Whether the list can move, and whether it sits at either end. Both flags
   * use a one-pixel tolerance because browsers report fractional scroll
   * positions after a smooth scroll.
   *
   * @param {number} scrollLeft
   * @param {number} scrollWidth
   * @param {number} clientWidth
   * @returns {{ hasOverflow: boolean, atStart: boolean, atEnd: boolean }}
   */
  function navState(scrollLeft, scrollWidth, clientWidth) {
    var maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) {
      return { hasOverflow: false, atStart: true, atEnd: true };
    }
    return {
      hasOverflow: true,
      atStart: scrollLeft <= 1,
      atEnd: scrollLeft >= maxScroll - 1,
    };
  }

  /**
   * Position of the thumb as percentages of the rail, or `null` when the list
   * does not overflow and the scrollbar should be hidden.
   *
   * @param {number} scrollLeft
   * @param {number} scrollWidth
   * @param {number} clientWidth
   * @returns {{ width: number, left: number } | null}
   */
  function progress(scrollLeft, scrollWidth, clientWidth) {
    var maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) return null;
    var width = Math.max(MIN_THUMB_PERCENT, (clientWidth / scrollWidth) * 100);
    var left = (scrollLeft / maxScroll) * (100 - width);
    return { width: width, left: left };
  }

  /**
   * Where the list should scroll to while the thumb is being dragged: the
   * pointer's travel along the rail, scaled from the room the thumb has on the
   * rail to the room the list has to scroll. Clamped to the list's range.
   *
   * @param {number} startScrollLeft scroll position when the thumb was grabbed
   * @param {number} deltaX pointer travel since the grab, in px
   * @param {number} railWidth width of the rail, in px
   * @param {number} thumbWidth width of the thumb, in px
   * @param {number} maxScroll scrollWidth minus clientWidth
   * @returns {number}
   */
  function thumbDragTarget(startScrollLeft, deltaX, railWidth, thumbWidth, maxScroll) {
    var room = railWidth - thumbWidth;
    if (!(room > 0) || !(maxScroll > 0)) return startScrollLeft;
    var target = startScrollLeft + (deltaX / room) * maxScroll;
    return Math.min(maxScroll, Math.max(0, target));
  }

  /**
   * A click on the rail outside the thumb pages towards the click: before the
   * thumb means back, after it means forward.
   *
   * @param {number} clickX click position along the rail, in px from its left edge
   * @param {number} thumbLeft thumb's left edge, in px from the rail's left edge
   * @param {number} thumbWidth
   * @returns {-1 | 0 | 1} direction, 0 when the click landed on the thumb
   */
  function railClickDirection(clickX, thumbLeft, thumbWidth) {
    if (clickX < thumbLeft) return -1;
    if (clickX > thumbLeft + thumbWidth) return 1;
    return 0;
  }

  /**
   * Whether a pointer that went down at `startX` and is now at `currentX` has
   * travelled far enough to be a drag. Below the threshold the movement is the
   * ordinary jitter of a click, and the click must still reach the product link.
   *
   * @param {number} startX
   * @param {number} currentX
   * @returns {boolean}
   */
  function isDrag(startX, currentX) {
    return Math.abs(currentX - startX) > DRAG_THRESHOLD_PX;
  }

  /**
   * Only the primary button (or a pen tip) starts anything.
   *
   * @param {{ button?: number }} event
   * @returns {boolean}
   */
  function isPrimaryPress(event) {
    return event.button === undefined || event.button === 0;
  }

  /**
   * Only mouse and pen pointers drag the row; touch keeps the browser's own
   * scrolling there. The scrollbar is different: it is not a scroll container,
   * so a finger on it drags the thumb like a mouse would (see the track's
   * `touch-action: none` in app.css).
   *
   * @param {{ pointerType?: string, button?: number }} event
   * @returns {boolean}
   */
  function startsDrag(event) {
    if (!isPrimaryPress(event)) return false;
    return event.pointerType !== 'touch';
  }

  /**
   * Distance between the first two slides, falling back to the first slide's
   * width when there is only one. Slides carry a horizontal margin on desktop,
   * so the width alone would undershoot the real step.
   *
   * @param {Element} list
   * @returns {number}
   */
  function measureSlideStep(list) {
    var slides = list.querySelectorAll('.slick-slide');
    if (slides.length === 0) return 0;
    var first = slides[0].getBoundingClientRect();
    if (slides.length === 1) return first.width;
    var second = slides[1].getBoundingClientRect();
    return second.left - first.left;
  }

  /**
   * A label the theme passed through Liquid's `t` filter. Shopify renders a key
   * that no locale file knows as the literal text "Translation missing: …", which
   * is truthy and would end up as the aria-label; treat it as absent instead.
   *
   * @param {unknown} value
   * @returns {string}
   */
  function usableLabel(value) {
    if (typeof value !== 'string' || value === '') return '';
    if (/^translation missing/i.test(value)) return '';
    return value;
  }

  /**
   * @returns {{ previous: string, next: string, scrollbar: string }}
   */
  function readLabels(options) {
    var fromOptions = (options && options.labels) || {};
    var fromWindow = (window.translations && window.translations.slider) || {};
    return {
      previous: usableLabel(fromOptions.previous) || usableLabel(fromWindow.previous) || FALLBACK_LABELS.previous,
      next: usableLabel(fromOptions.next) || usableLabel(fromWindow.next) || FALLBACK_LABELS.next,
      scrollbar: usableLabel(fromOptions.scrollbar) || usableLabel(fromWindow.scrollbar) || FALLBACK_LABELS.scrollbar,
    };
  }

  function createEdgeButton(document, direction, label) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = EDGE_BUTTON_CLASS + ' ' + EDGE_BUTTON_CLASS + '--' + direction;
    button.setAttribute('aria-label', label);
    var icon = document.createElement('i');
    // Both class names are spelled out so the Font Awesome subset scanner
    // (scripts/subset-fontawesome.py) sees the icons this file needs.
    icon.className = direction === 'previous' ? 'fa-sharp fa-solid fa-arrow-left' : 'fa-sharp fa-solid fa-arrow-right';
    icon.setAttribute('aria-hidden', 'true');
    button.appendChild(icon);
    return button;
  }

  function capturePointer(element, pointerId) {
    if (typeof element.setPointerCapture !== 'function') return;
    try {
      element.setPointerCapture(pointerId);
    } catch (_error) {
      // The pointer may already be gone; dragging still works without capture.
    }
  }

  function clearTextSelection() {
    if (!window.getSelection) return;
    var selection = window.getSelection();
    if (selection && typeof selection.removeAllRanges === 'function') selection.removeAllRanges();
  }

  function scrollListBy(list, delta) {
    if (typeof list.scrollBy === 'function') {
      list.scrollBy({ left: delta, behavior: 'smooth' });
    } else {
      list.scrollLeft += delta;
    }
  }

  /**
   * Attach drag-to-scroll, the scrollbar and the edge buttons to one slider
   * root. Safe to call again after Slick re-initialised the slider.
   *
   * @param {Element} slider the element carrying the `js-*-slider` class
   * @param {{ labels?: { previous?: string, next?: string, scrollbar?: string } }} [options]
   * @returns {{ list: Element, update: () => void, destroy: () => void } | null}
   */
  function bind(slider, options) {
    if (!slider) return null;
    var list = slider.querySelector('.slick-list');
    if (!list) return null;

    var existing = slider.florafySliderControls;
    if (existing) {
      if (existing.list === list) return existing;
      existing.destroy();
    }

    var document = slider.ownerDocument;
    var labels = readLabels(options);

    slider.classList.add(HOST_CLASS);

    // Scrollbar: a tall transparent track (the hit area) around a thin rail
    // that carries the thumb. The track is the interactive element.
    var controls = document.createElement('div');
    controls.className = CONTROLS_CLASS;
    if (!list.id) list.id = 'slider-list-' + (++listIdCounter);
    var track = document.createElement('div');
    track.className = TRACK_CLASS;
    track.setAttribute('role', 'scrollbar');
    track.setAttribute('aria-controls', list.id);
    track.setAttribute('aria-orientation', 'horizontal');
    track.setAttribute('aria-label', labels.scrollbar);
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('tabindex', '0');
    var rail = document.createElement('div');
    rail.className = RAIL_CLASS;
    var thumb = document.createElement('div');
    thumb.className = THUMB_CLASS;
    rail.appendChild(thumb);
    track.appendChild(rail);
    controls.appendChild(track);
    slider.appendChild(controls);

    // Edge buttons live on the slider root, positioned over the list.
    var previousButton = createEdgeButton(document, 'previous', labels.previous);
    var nextButton = createEdgeButton(document, 'next', labels.next);
    slider.appendChild(previousButton);
    slider.appendChild(nextButton);

    function placeEdgeButtons() {
      // Mid-height of the list, not of the root: the root also holds the scrollbar.
      var top = (list.offsetTop || 0) + (list.offsetHeight || 0) / 2;
      previousButton.style.top = top + 'px';
      nextButton.style.top = top + 'px';
    }

    function update() {
      var state = navState(list.scrollLeft, list.scrollWidth, list.clientWidth);
      if (!state.hasOverflow) {
        controls.style.display = 'none';
        previousButton.hidden = true;
        nextButton.hidden = true;
        return;
      }
      controls.style.display = '';
      var thumbPosition = progress(list.scrollLeft, list.scrollWidth, list.clientWidth);
      thumb.style.width = thumbPosition.width + '%';
      thumb.style.left = thumbPosition.left + '%';
      var maxScroll = list.scrollWidth - list.clientWidth;
      track.setAttribute('aria-valuemax', String(Math.round(maxScroll)));
      track.setAttribute('aria-valuenow', String(Math.round(list.scrollLeft)));
      previousButton.hidden = state.atStart;
      nextButton.hidden = state.atEnd;
    }

    function pageStep() {
      return scrollStep(list.clientWidth, measureSlideStep(list));
    }

    function onPreviousClick() {
      scrollListBy(list, -pageStep());
    }
    function onNextClick() {
      scrollListBy(list, pageStep());
    }

    // Drag the row. The pointer goes down without preventDefault so a plain
    // click still focuses and follows the product link; only once the pointer
    // has travelled past the threshold does the gesture become a drag, and
    // from then on the click that closes it is swallowed.
    var drag = null;
    var suppressClick = false;

    function onListPointerDown(event) {
      if (!startsDrag(event)) return;
      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: list.scrollLeft,
        active: false,
      };
    }

    function onListPointerMove(event) {
      if (!drag || event.pointerId !== drag.pointerId) return;
      if (!drag.active) {
        if (!isDrag(drag.startX, event.clientX)) return;
        drag.active = true;
        slider.classList.add(DRAGGING_CLASS);
        // The threshold lets the browser start a text selection before the
        // dragging class switches user-select off; drop that selection.
        clearTextSelection();
        capturePointer(list, drag.pointerId);
      }
      if (typeof event.preventDefault === 'function') event.preventDefault();
      list.scrollLeft = drag.startScrollLeft - (event.clientX - drag.startX);
    }

    function endListDrag(event) {
      if (!drag || (event && event.pointerId !== undefined && event.pointerId !== drag.pointerId)) return;
      var wasActive = drag.active;
      drag = null;
      if (!wasActive) return;
      slider.classList.remove(DRAGGING_CLASS);
      suppressClick = true;
      // The click that ends a drag is dispatched in the same task as pointerup,
      // so a zero-delay timer clears the flag only when no click followed.
      setTimeout(function () {
        suppressClick = false;
      }, 0);
    }

    function onListClickCapture(event) {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }

    function onListDragStart(event) {
      // Native image/link dragging cancels the pointer sequence mid-drag.
      if (drag) event.preventDefault();
    }

    // Scrollbar: drag the thumb, or click the rail to page towards the click.
    var thumbDrag = null;

    function onTrackPointerDown(event) {
      if (!isPrimaryPress(event)) return;
      // Preventing the press keeps a text selection from starting, but it also
      // stops the browser from focusing the track; focus it by hand so the
      // arrow keys work right after a click or a thumb drag.
      if (typeof event.preventDefault === 'function') event.preventDefault();
      if (typeof track.focus === 'function') track.focus();
      var railRect = rail.getBoundingClientRect();
      var thumbRect = thumb.getBoundingClientRect();
      var direction = railClickDirection(event.clientX - railRect.left, thumbRect.left - railRect.left, thumbRect.width);
      if (direction !== 0) {
        scrollListBy(list, direction * pageStep());
        return;
      }
      thumbDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: list.scrollLeft,
        railWidth: railRect.width,
        thumbWidth: thumbRect.width,
      };
      slider.classList.add(THUMB_DRAGGING_CLASS);
      slider.classList.add(DRAGGING_CLASS);
      clearTextSelection();
      capturePointer(track, event.pointerId);
    }

    function onTrackPointerMove(event) {
      if (!thumbDrag || event.pointerId !== thumbDrag.pointerId) return;
      if (typeof event.preventDefault === 'function') event.preventDefault();
      var maxScroll = list.scrollWidth - list.clientWidth;
      list.scrollLeft = thumbDragTarget(
        thumbDrag.startScrollLeft,
        event.clientX - thumbDrag.startX,
        thumbDrag.railWidth,
        thumbDrag.thumbWidth,
        maxScroll
      );
    }

    function endThumbDrag(event) {
      if (!thumbDrag || (event && event.pointerId !== undefined && event.pointerId !== thumbDrag.pointerId)) return;
      thumbDrag = null;
      slider.classList.remove(THUMB_DRAGGING_CLASS);
      slider.classList.remove(DRAGGING_CLASS);
    }

    function onTrackKeyDown(event) {
      var key = event.key;
      var delta = 0;
      if (key === 'ArrowLeft') delta = -pageStep();
      else if (key === 'ArrowRight') delta = pageStep();
      else if (key === 'Home') delta = -list.scrollLeft;
      else if (key === 'End') delta = list.scrollWidth;
      else return;
      if (typeof event.preventDefault === 'function') event.preventDefault();
      scrollListBy(list, delta);
    }

    function onResize() {
      placeEdgeButtons();
      update();
    }

    previousButton.addEventListener('click', onPreviousClick);
    nextButton.addEventListener('click', onNextClick);
    list.addEventListener('scroll', update, { passive: true });
    list.addEventListener('pointerdown', onListPointerDown);
    list.addEventListener('pointermove', onListPointerMove);
    list.addEventListener('pointerup', endListDrag);
    list.addEventListener('pointercancel', endListDrag);
    list.addEventListener('lostpointercapture', endListDrag);
    list.addEventListener('click', onListClickCapture, true);
    list.addEventListener('dragstart', onListDragStart);
    track.addEventListener('pointerdown', onTrackPointerDown);
    track.addEventListener('pointermove', onTrackPointerMove);
    track.addEventListener('pointerup', endThumbDrag);
    track.addEventListener('pointercancel', endThumbDrag);
    track.addEventListener('lostpointercapture', endThumbDrag);
    track.addEventListener('keydown', onTrackKeyDown);

    // Re-measure when the list changes size: a width change (app.js also
    // re-initialises Slick then) or a height change once images have loaded,
    // which moves the edge buttons' mid-height. A ResizeObserver sees both and
    // can be disconnected cleanly. The window fallback deliberately bypasses
    // the theme's patched window.addEventListener: that patch wraps a resize
    // listener in a debounce closure, so the reference handed to
    // removeEventListener never matches and every Slick re-init would leak a
    // handler.
    var resizeObserver = null;
    var nativeAdd = window.EventTarget && window.EventTarget.prototype.addEventListener;
    var nativeRemove = window.EventTarget && window.EventTarget.prototype.removeEventListener;
    if (typeof window.ResizeObserver === 'function') {
      resizeObserver = new window.ResizeObserver(onResize);
      resizeObserver.observe(list);
    } else if (typeof nativeAdd === 'function') {
      nativeAdd.call(window, 'resize', onResize);
    } else {
      window.addEventListener('resize', onResize);
    }

    function destroy() {
      previousButton.removeEventListener('click', onPreviousClick);
      nextButton.removeEventListener('click', onNextClick);
      list.removeEventListener('scroll', update);
      list.removeEventListener('pointerdown', onListPointerDown);
      list.removeEventListener('pointermove', onListPointerMove);
      list.removeEventListener('pointerup', endListDrag);
      list.removeEventListener('pointercancel', endListDrag);
      list.removeEventListener('lostpointercapture', endListDrag);
      list.removeEventListener('click', onListClickCapture, true);
      list.removeEventListener('dragstart', onListDragStart);
      track.removeEventListener('pointerdown', onTrackPointerDown);
      track.removeEventListener('pointermove', onTrackPointerMove);
      track.removeEventListener('pointerup', endThumbDrag);
      track.removeEventListener('pointercancel', endThumbDrag);
      track.removeEventListener('lostpointercapture', endThumbDrag);
      track.removeEventListener('keydown', onTrackKeyDown);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else if (typeof nativeRemove === 'function') {
        nativeRemove.call(window, 'resize', onResize);
      } else {
        window.removeEventListener('resize', onResize);
      }
      slider.classList.remove(DRAGGING_CLASS);
      slider.classList.remove(THUMB_DRAGGING_CLASS);
      slider.classList.remove(HOST_CLASS);
      [controls, previousButton, nextButton].forEach(function (node) {
        if (node.parentNode) node.parentNode.removeChild(node);
      });
      if (slider.florafySliderControls === handle) slider.florafySliderControls = undefined;
    }

    var handle = { list: list, update: onResize, destroy: destroy };
    slider.florafySliderControls = handle;

    onResize();
    return handle;
  }

  window.florafySliderControls = {
    bind: bind,
    scrollStep: scrollStep,
    navState: navState,
    progress: progress,
    thumbDragTarget: thumbDragTarget,
    railClickDirection: railClickDirection,
    isDrag: isDrag,
    startsDrag: startsDrag,
    DRAG_THRESHOLD_PX: DRAG_THRESHOLD_PX,
  };
})();
